import { TwoUIProgressViewOperationData } from "@amx/ux-twoui-telemetry";
import { HighContrastSelector, IStackItemStyles, Stack, StackItem } from "@fluentui/react";
import React from "react";
import { BaseProps } from "../../../shared/props";
import { isMobileViewport, useViewport } from "../../../styles";
import { Media } from "../../Media/Media";
import { MediaProps } from "../../Media/Media.types";
import { ShimmerProgressView } from "../../Shimmer/ShimmerProgressView/ShimmerProgressView";
import { FeatureContentShimmer } from "./FeatureContentShimmer/FeatureContentShimmer";
import { FeatureHeading, FeatureHeadingProps } from "./FeatureHeading";

export interface FeatureMediaGroupContentProps extends BaseProps {
    /**
     * Feature Variant header text
     */
    readonly featureHeading?: FeatureHeadingProps;
    /**
     * Media List for feature content slot.
     */
    readonly mediaPropsList?: readonly MediaProps[];
    /**
     * Contains operation data.
     */
    readonly operationData?: TwoUIProgressViewOperationData;
}
const getMediaListStyles = (isMobile: boolean): IStackItemStyles => {
    if (isMobile) {
        return {
            root: {
                padding: "0px 5px 5px",
                maxWidth: "276px",
                minWidth: "184px",
                boxSizing: "border-box",
                flex: "1 1 50%",
                [HighContrastSelector]: {
                    ":focus-within": {
                        outline: "2px auto highlight"
                    },
                }
            }
        };
    } else {
        return {
            root: {
                padding: "0px 5px 5px",
                boxSizing: "border-box",
                flex: "1 1 50%",
                [HighContrastSelector]: {
                    ":focus-within": {
                        outline: "2px auto highlight"
                    },
                }
            }
        };
    }
};

export const FeatureMediaGroupContent: React.FC<FeatureMediaGroupContentProps> = ({
    featureHeading,
    mediaPropsList,
    operationData
}) => {
    const viewportType = useViewport();
    const isMobile = isMobileViewport(viewportType);

    let header: JSX.Element | undefined;
    if (featureHeading) {
        header = (<FeatureHeading {...featureHeading} />);
    }

    const content = mediaPropsList?.map((item: MediaProps) => {
        return (
            <StackItem styles={getMediaListStyles(isMobile)}>
                <Media image={item.image} size={item.size} linkProps={item.linkProps}>
                    {item.children}
                </Media>
            </StackItem>
        );
    });

    const contentBlock = (
        <Stack>
            <Stack styles={{ root: { padding: "0px 0px 12px 0px" } }}>
                {header}
            </Stack>
            <Stack horizontal={true} wrap={true} >
                {content}
            </Stack>
        </Stack>
    );

    if (operationData) {
        return (
            <ShimmerProgressView
                operationData={operationData}
                firstLoadView={<FeatureContentShimmer />}
                finishedView={contentBlock}
                failedView={undefined}
            />
        );
    } else {
        return contentBlock;
    }
};
